import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, CalendarIcon, DotDotDotVerticalIcon, UnmutedCameraIcon, } from '@streamr/tv-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { BroadcastFrame, BroadcastFrameList, } from '../components/frames/BroadcastFrame';
import { LobbyFrame } from '../components/frames/LobbyFrame';
import { CenteredContentContainer } from '../components/layout/content/CenteredContentContainer';
import { HeaderWithLeftLogoAndControls } from '../components/layout/headers/HeaderWithLeftLogoAndControls';
import { PageLayout } from '../components/layout/PageLayout';
import { showBroadcastInfoEditPopup } from '../components/popups/BroadcastInfoEditPopup';
import { showSettingsPopup } from '../components/popups/SettingsPopup';
import { BroadcastStateService } from '../services/broadcaststate/BroadcastStateService';
import { ScheduleService } from '../services/schedule/ScheduleService';
import { TimeService } from '../services/time/TimeService';
import { trackPageView } from '../utils/matomo';
import { formBroadcastPath, formBroadcastUrl, parseBroadcastFromUrl, } from '../utils/url';
// Track when the script is loaded in production
// this emulates the behavior of embedding the tracking
// script in the HTML.
trackPageView();
function useThrottledTime(intervalInMillis = 5000) {
    const now = TimeService.getOutput().useTime();
    const [throttled, setThrottled] = useState(() => now ?? Date.now());
    useEffect(() => {
        if (now !== undefined && now - throttled >= intervalInMillis) {
            setThrottled(now);
        }
    }, [now, throttled, intervalInMillis]);
    return throttled;
}
const HeaderControls = ({ broadcastAmount, onActivateBroadcastClick, }) => {
    const anchorRef = useRef(null);
    return (_jsxs(_Fragment, { children: [broadcastAmount > 0 && (_jsxs(HeaderButtons, { children: [_jsx(BaseButton, { type: "button", kind: "glass", title: "Schedule broadcast", onClick: () => {
                            showBroadcastInfoEditPopup();
                        }, children: _jsx(CalendarIcon, {}) }), _jsx(BroadcastHeaderButton, { type: "button", title: "Start broadcast", onClick: () => {
                            onActivateBroadcastClick(undefined, true);
                        }, children: _jsx(UnmutedCameraIcon, { fill: "currentColor" }) })] })), _jsx("div", { ref: anchorRef, children: _jsx(BaseButton, { type: "button", onClick: () => {
                        // biome-ignore lint/nursery/noFloatingPromises: TODO
                        showSettingsPopup({
                            anchorRef,
                        });
                    }, children: _jsx(DotDotDotVerticalIcon, {}) }) })] }));
};
export function HomePage() {
    const broadcasts = ScheduleService.getOutput().useScheduledBroadcasts();
    const time = useThrottledTime(30000);
    const [pastBroadcasts, upcomingBroadcasts] = useMemo(() => {
        const past = [];
        const upcoming = [];
        for (const broadcast of broadcasts) {
            (broadcast.startsAt < time ? past : upcoming).push(broadcast);
        }
        past.reverse();
        return [past, upcoming];
    }, [broadcasts, time]);
    const navigate = useNavigate();
    const activateBroadcast = useCallback((savedBroadcast, navigateToBroadcastPage) => {
        BroadcastStateService.getNotificationInterface().notifyActivateBroadcast(savedBroadcast, navigateToBroadcastPage ? navigate : undefined);
    }, [navigate]);
    useEffect(() => {
        // Support legacy broadcast URLs TODO remove within few months
        const broadcast = parseBroadcastFromUrl(new URL(window.location.href));
        if (broadcast !== undefined) {
            // We are now at home page (i.e. at route /), but URL parser
            // says that we should be on a broadcast page. The reason must be
            // that we are using legacy hash-router URLS. Therefore we
            // form a URL with the current broadcast URL format and redirect
            // the user there.
            console.log('Redirect legacy URL');
            window.location.href = formBroadcastUrl(broadcast);
        }
    }, []);
    return (_jsx(PageLayout, { header: _jsx(HeaderWithLeftLogoAndControls, { controls: _jsx(HeaderControls, { broadcastAmount: broadcasts.length, onActivateBroadcastClick: activateBroadcast }) }), children: _jsxs(CenteredContentContainer, { children: [broadcasts.length === 0 && _jsx(LobbyFrame, {}), upcomingBroadcasts.length > 0 && (_jsx(BroadcastFrameList, { multi: broadcasts.length > 1, title: "Upcoming", children: upcomingBroadcasts.map((broadcast) => (_jsx(BroadcastFrame, { broadcast: broadcast, onActivateBroadcastClick: () => {
                            activateBroadcast(broadcast, true);
                        } }, broadcast.id))) })), pastBroadcasts.length > 0 && (_jsx(BroadcastFrameList, { multi: broadcasts.length > 1, title: upcomingBroadcasts.length > 0 ? 'Past' : undefined, children: pastBroadcasts.map((broadcast) => (_jsx(BroadcastFrame, { broadcast: broadcast, onActivateBroadcastClick: () => {
                            activateBroadcast(broadcast, true);
                        }, onRecordingsClick: () => {
                            activateBroadcast(broadcast, false);
                            navigate(formBroadcastPath(broadcast, '/recordings'));
                        } }, broadcast.id))) }))] }) }));
}
const BaseButton = styled(Button) `
    --Button_Width: 3rem;
    --Button_Height: 3rem;
    --Button_BorderRadius: 0.75rem;
`;
const BroadcastHeaderButton = styled(BaseButton) `
    --Button_HoverBackground: var(--tv-ui-color-3);
    --Button_ActiveBackground: var(--tv-ui-color-3);
    background: var(--tv-ui-color-3);
    color: var(--tv-ui-color-1);
`;
const HeaderButtons = styled.div `
    align-items: center;
    display: grid;
    gap: 1rem;
    grid-template-columns: auto auto;
    justify-items: end;

    @media (min-width: 37.5rem) {
        grid-template-columns: auto auto;
    }
`;
